// https://material-ui.com/api/text-field/
// https://github.com/text-mask/text-mask/blob/master/componentDocumentation.md#readme

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import withGuid from "../../hocs/with_guid.js";
import withHelperText from "../../hocs/with_helper_text.js";

import { TextField as MaterialTextField, FormLabel } from '@material-ui/core';
import MaskedInput from 'react-text-mask';

import { 
  InputVariantStandard, InputVariantOutlined, InputVariantFilled,
  InputLabelDisplayModeInside, InputLabelDisplayModeAbove,
  TextFieldTypePhone
} from "./constants.js.jsx";

const styles = theme => ({
});

class PhoneMask extends React.Component {

  render() {
    const { inputRef, mask, onChange, value, ...other } = this.props;
    return <MaskedInput {...other}
                        ref = {ref => {
                          inputRef(ref ? ref.inputElement : null);
                        }}
                        mask = { mask }
                        placeholderChar = { '\u2000' }
                        showMask = { true }
                        guide = { false } 
                        onChange = { onChange }
                        value = { value} />
  }
}

/**
 * Callback for inputs
 * @callback PhoneField~onChange
 * @param {string} value input's new value
 */

/**
 * Mask for phone field
 * 
 * Use RegExp element for define variable character of phone and use string element for constant character
 * 
 * Example: for number with like this "+7 (911) 111-11-11", 
 * you might use mask equals ["+7", " ", "(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/]
 * @typedef {Array.<string|RegExp>} PhoneField~Mask
 * 
 */

/**
 * Component for input formatted phone number. Already includes HOCs {@link WithGuid}, {@link WithHelperText}
 * @name PhoneField
 * @param {object} props React component properties
 * @param {string} [props.value] Field's value
 * @param {Array.<PhoneField~Mask>} props.mask Mask for phone formatting.
 * @param {string} [props.displayName] Field's display name
 * @param {boolean} [props.hasErrors = false] Does errors exists on the field?
 * @param {Array.<string>} [props.errors = []] Error messages array
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @param {string} [props.className] CSS-class of button
 * @param {string} [props.variant = "outlined"] View variant for text field stylization. Available values:
 *  - "standard"
 *  - "outlined"
 *  - "filled"
 * 
 * Explanation of variants: {@link https://material-ui.com/components/text-fields/#textfield}
 * 
 * NOTE: for providing variant, please, import necessary variant as constant from file "components/base/inputs/constants.js.jsx"
 * @param {string} [props.displayNamePosition = "inside"] Position of field's display name: inside the field with floating style or above. Available values:
 *  - "inside"
 *  - "above"
 * 
 * NOTE: for providing position, please, import necessary position as constant from file "components/base/inputs/constants.js.jsx"
 * @param {PhoneField~onChange} [props.onChange] On change handler
 * @example
 * return (
 *   <PhoneField className={ "awesome-class" } 
 *              onChange = { (value) => {
 *                // Some actions
 *              } } 
 *              value = { "+7 (911) 111-11-11" }
 *              mask = { ["+7", " ", "(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/] } />
 * )
*/
class PhoneField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      
    }

    this.onChange = this.onChange.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    let propsValue   = `${props.value || ""}`;
    let defaultValue = `${state.defaultValue || ""}`;
    let stateValue   = `${state.value || ""}`;
    
    if(defaultValue !== propsValue) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      value = stateValue;
    }

    return {
      value: value,
      defaultValue: defaultValue
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.value !== nextState.value ||
           this.props.displayName !== nextProps.displayName ||
           this.props.hasError !== nextProps.hasError ||
           this.props.readOnly !== nextProps.readOnly;
  }

  onChange(event) {
    const { onChange } = this.props;
    let value = event.target.value;
    this.setState({
      value: value
    });

    if(onChange) {
      onChange(value);
    }
  }

  render() {
    const { classes, className, guid, hasError, readOnly } = this.props;

    const { name, displayName, variant, underline, displayNamePosition, mask, disableAutoComplete } = this.props;

    const { value } = this.state;

    let inputProps = {
      inputComponent: PhoneMask,
      inputProps: {
        value: value ? value : "",
        mask: mask,
        onChange: this.onChange
      }
      
    };
    if(variant === InputVariantFilled) {
      inputProps.disableUnderline = !underline;
    }

    return <React.Fragment>
      {
        displayNamePosition === InputLabelDisplayModeAbove &&
        <FormLabel htmlFor = { guid }
                   error = { hasError } >
          { `${displayName}:` }
        </FormLabel>
      }
      <MaterialTextField className = { clsx(classes.root, className) } 
                         id = { guid } 
                         fullWidth
                         error = { hasError }
                         InputProps = { inputProps }
                         type = { TextFieldTypePhone }
                         name = { disableAutoComplete ? guid : name }
                         label = { displayNamePosition === InputLabelDisplayModeInside ? displayName : undefined }
                         variant = { variant }
                         disabled = { readOnly } />
    </React.Fragment>
  }
}

PhoneField.propTypes = {
  variant: PropTypes.oneOf([InputVariantStandard, InputVariantOutlined, InputVariantFilled]),
  displayNamePosition: PropTypes.oneOf([InputLabelDisplayModeAbove, InputLabelDisplayModeInside]),
  readOnly: PropTypes.bool,
  displayName: PropTypes.string,
  mask: PropTypes.array.isRequired,
  value: PropTypes.string
};

PhoneField.defaultProps = {
  variant: InputVariantOutlined,
  displayNamePosition: InputLabelDisplayModeInside,
  underline: true,
  readOnly: false
}
export default compose(withGuid, withHelperText, withStyles(styles))(PhoneField)
